import { useBCMSModal, useWorkflowStep } from "bcms-react";
import CustomSelect, { CustomAccountSelect, CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView, WidgetContainer, WidgetView } from 'components/shared';
import { CheckmarkBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton, SecondaryButton, UIAmountInput } from "components/ui";
import QuickTransferWorkflow, { getDefaultTransfer, getWorkflowSteps } from "components/workflows/QuickTransferWorkflow";
import { Account } from "model";
import React, { useState } from "react";
import "./styles.css";
/**
 * @function QuickTransferWidget
 * @return {React.Component}
 * @exported true
 * @description This is the quick transfer widget
 */
export default function QuickTransferWidget(props) {
  return(
    <WidgetContainer className="quick-transfer-widget">
      <WidgetView text="Quick Transfer" data-point="Quick_Transfer_Widget">
        <QuickTransferWorkflow />
      </WidgetView>
    </WidgetContainer>
  );
}
/**
 * @function QuickTransferContent
 * @returns {React.Component}
 * @description The content of the quick transfer widget
 * @example
 * <QuickTransferContent />
 */
export function QuickTransferContent(props) {
  // Initalize the workflowStep
  const workflowStep = useWorkflowStep(getDefaultTransfer, getWorkflowSteps);
  const { open } = useBCMSModal();
  // Read the transfer from the workflow state
  const { transfer, success } = workflowStep.getStepState;
  const update = prop => value => workflowStep.update(["transfer", prop], value);
  // Set properties to determine what fields to show
  const showPaymentOption = transfer.to !== undefined && transfer.to.type === "loan";
  const paymentOptionPaymentDueSelected = transfer.option === "Payment Due";
  const paymentOptionSelected = transfer.option !== undefined;
  const showAmount = !showPaymentOption || (showPaymentOption && paymentOptionSelected && !paymentOptionPaymentDueSelected);
  const paymentAmount = transfer.to !== undefined && Account.initByType(transfer.to.id).lastPayment;
  // Filter the accounts so no loans show in the from account dropdown
  const accountItems = Account.initAll().filter(item => item.type === "checking" || item.type === "savings", []);
  const toSelected = transfer.to && transfer.to.id ? Account.initByType(transfer.to.id) : undefined;
  // on submit update the transfer to set defaults and open the review model
  const submit = evt => {
    workflowStep.getStepState.transfer.setDefaults();
    open("ReviewTransferModal", workflowStep);
  }
  const clear = evt => workflowStep.workflow.update(workflowStep.defaultState);
  const updateSuccess = () => workflowStep.update(["success"], false);
  
  return (
    <>
      <FormContainer>
        <FormRowView text="From">
          <CustomAccountSelect accounts={ accountItems } value={ transfer.from } onChange={ update("from") } dataPoint="Quick_Transfer_From" />
        </FormRowView>

        <FormRowView text="To">           
          <CustomAccountSelect accounts={ Account.initAll() } value={ toSelected } disabled={ transfer.from } onChange={ update("to") } dataPoint="Quick_Transfer_To" />                    
        </FormRowView>

        { showPaymentOption &&
          <FormRowView text="Payment Option">
            <CustomSelect value={ transfer.option } onChange={ update("option") }>
              <CustomSelectItem value="Payment Due">Payment Due { paymentAmount.format() }</CustomSelectItem>
              <CustomSelectItem value="Other Amount">Other Amount</CustomSelectItem>
              <CustomSelectItem value="Principal-Only Payment">Principal-Only Payment</CustomSelectItem>
              <CustomSelectItem value="Interest-Only Payment">Interest-Only Payment</CustomSelectItem>
              <CustomSelectItem value="Escrow-Only Payment">Escrow-Only Payment</CustomSelectItem>
            </CustomSelect>
          </FormRowView> }

        { showAmount &&
          <FormRowView text="Amount">
            <UIAmountInput value={ transfer.amount.amount ?? "" } onChange={ update("amount") } formatOnBlur={ false } dataPoint="Quick_Transfer_Amount" />
          </FormRowView> }

        <FormRowView>
          <PrimaryButton text="Transfer" onClick={ submit } dataPoint="Quick_Transfer_Submit" />
          <SecondaryButton text="Clear" onClick={ clear } />
        </FormRowView>
      </FormContainer>

      { success && 
        <CheckmarkBanner text="Your transfer has been successfully submitted. Confirmation number: 18" onClick={ updateSuccess } /> 
      }
    </>
  );
}